<script setup lang="ts">
import BasePage from '@/components/pages/BasePage/BasePage.vue';
import BasePageTitle from '@/components/pages/BasePage/BasePageTitle.vue';
import VeeSubmitButton from '@/components/forms/SubmitButton/VeeSubmitButton.vue';
import MedicalSystemForm from './components/MedicalSystemForm.vue';
import { useVeeForm } from '@/components/forms/useVeeForm';
import { api } from '@/api';
import { routenames } from '@/router/routenames';
import { computed, onMounted } from 'vue';
import { type MedicalSystemRtSystemDto } from '@/api/generator';
import { useLoading } from '@/utils/useLoading';
import BasePageStatus from '@/components/pages/BasePage/BasePageStatus.vue';
import { useRouter } from 'vue-router';
import { useToken } from '@/utils/tokens/tokens';
import { getMedicalSystemSubtitle } from './utils';
import { toCreateMedicalSystemRtSystemDto } from './mappings';
import PtwCancelButton from '@/components/buttons/PtwCancelButton.vue';
import { entityNotFoundErrorHandler, formBackendErrorHandler } from '@/errorHandling';

const props = defineProps<{
  id: string;
}>();

const router = useRouter();

const { form } = useVeeForm<MedicalSystemRtSystemDto>({
  schemaName: 'MedicalSystemRtSystemDto',
  mode: 'edit',
});

const { handleSubmit, values, resetForm } = form;

const submit = handleSubmit(
  async (medicalSystemRtSystemDto) => {
    const { success } = await api.medicalSystemsApi.updateMedicalSystemRtSystem(
      {
        id: props.id,
        createMedicalSystemRtSystemDto: toCreateMedicalSystemRtSystemDto(medicalSystemRtSystemDto),
      },
      [formBackendErrorHandler(form)],
    );

    if (success) {
      await router.push({ name: routenames.medicalSystems.overview });
    }
  },
  {
    withReferenceCheck: true,
  },
);

const {
  action: loadInitialValues,
  actionWithoutLoading: silentLoading,
  loading,
} = useLoading(async () => {
  const { success, result } = await api.medicalSystemsApi.getMedicalSystemRtSystem(props, [
    entityNotFoundErrorHandler(router),
  ]);
  if (success) resetForm({ values: result });
}, true);

onMounted(async () => {
  await silentLoading();
  await token.request();
});

const token = useToken(values);

token.on.initialized(loadInitialValues);

token.on.inUse(() => {
  router.push({
    name: routenames.medicalSystems.overview,
  });
});

const subtitle = computed(() => getMedicalSystemSubtitle(values));
</script>

<template>
  <form @submit="submit">
    <BasePage :loading="loading">
      <template #title>
        <BasePageTitle :title="values.name" :subtitle="subtitle" :titleId="values.iuid" />
      </template>

      <template #status>
        <BasePageStatus :entity="values" />
      </template>

      <template #actions>
        <PtwCancelButton :to="{ name: routenames.medicalSystems.overview }" />
        <VeeSubmitButton class="ml-4" />
      </template>

      <MedicalSystemForm />
    </BasePage>
  </form>
</template>
